import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  AttendanceCourseSummary,
  Class,
  NOTIFICATION_TYPE,
  PROGRAM_APPEAL_DECISION,
  PROGRAM_APPEAL_STATUS,
  ProgramAppeal,
  Student,
} from "../models";
import { notificationService } from "../services/notification-service";
import { logger } from "../utils/logger";

interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
}

interface AuthRequest extends Request {
  user?: { userId: string; role: string; schoolId?: string };
}

function formatAppeal(appeal: InstanceType<typeof ProgramAppeal>) {
  return {
    id: appeal.id,
    studentId: appeal.studentId?.toString(),
    classId: appeal.classId?.toString(),
    className: appeal.className,
    reason: appeal.reason,
    documents: appeal.documents,
    status: appeal.status,
    decision: appeal.decision ?? null,
    reviewNotes: appeal.reviewNotes ?? null,
    reviewedAt: appeal.reviewedAt ?? null,
    createdAt: appeal.createdAt,
  };
}

class ProgramAppealController {
  async createProgramAppeal(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, reason } = req.body;
      const files = (req as Request & { files?: Express.Multer.File[] }).files;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const trimmedReason = String(reason ?? "").trim();
      if (!classId || !trimmedReason) {
        res.status(400).json({
          success: false,
          message: "classId and reason are required",
        });
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const classItem = await Class.findOne({
        _id: classId,
        studentIds: student._id,
      });
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found or not enrolled",
        });
        return;
      }

      const summary = await AttendanceCourseSummary.findOne({
        classId: classItem._id,
        studentId: student._id,
      });
      if (!summary?.attendanceFlagged) {
        res.status(422).json({
          success: false,
          message: "Program appeal requires attendance to be flagged below 20%",
        });
        return;
      }

      const existingPending = await ProgramAppeal.findOne({
        studentId: student._id,
        classId: classItem._id,
        status: PROGRAM_APPEAL_STATUS.PENDING,
      });
      if (existingPending) {
        res.status(409).json({
          success: false,
          message: "A pending program appeal already exists for this course",
        });
        return;
      }

      const documents =
        files?.map(file => ({
          fileName: file.filename,
          originalName: file.originalname,
          fileSize: file.size,
          mimeType: file.mimetype,
          fileUrl: `/uploads/documents/${file.filename}`,
          uploadedAt: new Date(),
        })) ?? [];

      const appeal = await ProgramAppeal.create({
        studentId: student._id,
        schoolId: student.schoolId,
        classId: classItem._id,
        className: classItem.className,
        reason: trimmedReason,
        documents,
        status: PROGRAM_APPEAL_STATUS.PENDING,
      });

      res.status(201).json({
        success: true,
        message: "Program appeal submitted successfully",
        data: { appeal: formatAppeal(appeal) },
      } as APIResponse);
    } catch (error) {
      logger.error("Create program appeal error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to submit program appeal",
      } as APIResponse);
    }
  }

  async getStudentProgramAppeal(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { id } = req.params;

      if (!userId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });
      if (!student) {
        res.status(404).json({ success: false, message: "Student not found" });
        return;
      }

      const appeal = await ProgramAppeal.findOne({
        _id: id,
        studentId: student._id,
      });
      if (!appeal) {
        res
          .status(404)
          .json({ success: false, message: "Program appeal not found" });
        return;
      }

      res.status(200).json({
        success: true,
        message: "Program appeal retrieved successfully",
        data: { appeal: formatAppeal(appeal) },
      } as APIResponse);
    } catch (error) {
      logger.error("Get student program appeal error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve program appeal",
      } as APIResponse);
    }
  }

  async getSchoolProgramAppeals(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const schoolId = req.user?.schoolId;
      const { status, page = 1, limit = 20 } = req.query;

      if (!schoolId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const appealFilter: Record<string, unknown> = {
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };
      if (status) appealFilter.status = status;

      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;

      const [appeals, total] = await Promise.all([
        ProgramAppeal.find({ ...appealFilter })
          .populate("studentId", "fullName studentId profilePicture")
          .sort({ createdAt: -1 })
          .skip(skip)
          .limit(limitNum),
        ProgramAppeal.countDocuments({ ...appealFilter }),
      ]);

      res.status(200).json({
        success: true,
        message: "Program appeals retrieved successfully",
        data: {
          appeals: appeals.map(a => ({
            ...formatAppeal(a),
            student: a.studentId,
          })),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get school program appeals error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve program appeals",
      } as APIResponse);
    }
  }

  async resolveProgramAppeal(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const { id } = req.params;
      const { decision, notes } = req.body;

      if (!userId || !schoolId) {
        res.status(401).json({ success: false, message: "Unauthorized" });
        return;
      }

      const validDecisions = Object.values(PROGRAM_APPEAL_DECISION);
      if (!decision || !validDecisions.includes(decision)) {
        res.status(400).json({
          success: false,
          message: `decision must be one of: ${validDecisions.join(", ")}`,
        });
        return;
      }

      const appeal = await ProgramAppeal.findOne({
        _id: id,
        schoolId: new mongoose.Types.ObjectId(schoolId),
        status: PROGRAM_APPEAL_STATUS.PENDING,
      });
      if (!appeal) {
        res
          .status(404)
          .json({ success: false, message: "Program appeal not found" });
        return;
      }

      appeal.status = PROGRAM_APPEAL_STATUS.RESOLVED;
      appeal.decision = decision;
      appeal.reviewNotes = notes;
      appeal.reviewedBy = new mongoose.Types.ObjectId(userId);
      appeal.reviewedAt = new Date();
      await appeal.save();

      const student = await Student.findById(appeal.studentId);
      if (student) {
        await notificationService.createNotification(
          student.userId,
          NOTIFICATION_TYPE.REMOVAL_NOTICE,
          "Program Appeal Resolved",
          `Your program appeal decision: ${decision}`,
          {
            metadata: {
              appealId: appeal.id,
              classId: appeal.classId.toString(),
              decision,
            },
            schoolId: appeal.schoolId,
          },
        );
      }

      res.status(200).json({
        success: true,
        message: "Program appeal resolved successfully",
        data: { appeal: formatAppeal(appeal) },
      } as APIResponse);
    } catch (error) {
      logger.error("Resolve program appeal error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to resolve program appeal",
      } as APIResponse);
    }
  }
}

export const programAppealController = new ProgramAppealController();
